import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { saveAs } from 'file-saver';
import { ContactAutocompleteResult } from 'src/app/services/contacts/contact-autocomplete.service';
import { CustomerFinancialReportService, ReportCandidate, ReportPreview, ReportResolutionItem } from './customer-financial-report.service';

@Component({
  selector: 'app-customer-financial-report',
  templateUrl: './customer-financial-report.component.html',
  styleUrl: './customer-financial-report.component.scss'
})
export class CustomerFinancialReportComponent implements OnInit, OnDestroy {
  theme: 'dark' | 'light' = localStorage.getItem('ulaap-customer-reports-theme') === 'light'
    || (!localStorage.getItem('ulaap-customer-reports-theme') && localStorage.getItem('ulaap-fsc-theme') === 'light') ? 'light' : 'dark';
  customer = 'b0c472a0-e85a-439a-9dbc-2ae40304a9f5';
  billingAccounts: any[] = [];
  billingAccount = '';
  loadBillingAccounts(): void {
    const customer = this.customer; this.billingAccounts = []; this.billingAccount = ''; this.reports.billingAccount = '';
    this.reports.accounts(customer).subscribe({ next: result => {
      if (customer !== this.customer) return;
      this.billingAccounts = result.accounts || [];
      this.billingAccount = this.billingAccounts.find(a => a.code === 'standard')?.billing_account_uuid || '';
      this.billingChanged();
    }, error: () => { this.error = 'Could not load billing accounts. Refresh before creating a report.'; } });
  }
  billingChanged(): void {
    this.reports.billingAccount = this.billingAccount; this.selected.clear(); this.selectionChanged(); this.loadData(true);
  }
  customerName = 'TECHNOGYM';
  customerControl = new FormControl<unknown>('TECHNOGYM');
  selected = new Map<string, ReportCandidate>();
  selectedRows: ReportCandidate[] = [];
  selectedOnly = false;
  pasteText = '';
  pasteSummary = '';
  pasteIssues: ReportResolutionItem[] = [];
  resolving = false;
  rowData: ReportCandidate[] = [];
  preview: ReportPreview | null = null;
  error = '';
  success = '';
  loading = false;
  previewing = false;
  creating = false;
  offset = 0;
  hasMore = false;
  search = '';
  private grid?: GridApi<ReportCandidate>;
  private listRequest?: Subscription;
  private actionRequest?: Subscription;
  private requestKey = '';
  private completedDownload: Blob | null = null;
  private completedFilename = '';

  defaultColDef: ColDef = { sortable: true, resizable: true, filter: 'agTextColumnFilter', minWidth: 130 };
  gridOptions: GridOptions<ReportCandidate> = { getRowId: params => params.data.lading_uuid, rowHeight: 44 };
  columnDefs: ColDef<ReportCandidate>[] = [
    { headerName: 'Select', colId: 'selection', width: 105, minWidth: 105, pinned: 'left', sortable: false, filter: false,
      cellRenderer: (params: ICellRendererParams<ReportCandidate>) => this.selectionButton(params) },
    { field: 'alternate_reference', headerName: 'Reference', width: 155 },
    { field: 'customer_po', headerName: 'Customer PO', width: 165 },
    { field: 'consignee_name', headerName: 'Consignee', flex: 1, minWidth: 200 },
    { field: 'delivery_date', headerName: 'Delivery date', width: 150 },
    { field: 'lading_status', headerName: 'Status', width: 160 },
    { field: 'eligible', headerName: 'Report readiness', width: 250,
      valueGetter: params => params.data?.eligible ? 'Ready' : params.data?.exclusion_reason },
    { field: 'audited', cellDataType: false, headerName: 'Audit', width: 140, valueFormatter: params => params.value ? 'Audited' : 'Unaudited' },
    { field: 'previously_exported', cellDataType: false, headerName: 'Prior export', width: 145, valueFormatter: params => params.value ? 'Exported before' : 'Not exported' },
    { headerName: 'Financials', colId: 'financials', sortable: false, filter: false, width: 130,
      cellRenderer: (params: ICellRendererParams<ReportCandidate>) => {
        const link = document.createElement('a');
        link.textContent = 'Open financials';
        link.href = '/financials/' + encodeURIComponent(params.data?.lading_uuid || '');
        link.target = '_blank'; link.rel = 'noopener'; link.className = 'cfr-grid-link';
        return link;
      } }
  ];
  previewColumns: ColDef[] = [
    { field: 'alternate_reference', headerName: 'Reference', width: 160 },
    { field: 'delivery', headerName: 'Customer PO', width: 170 },
    { field: 'service_name', headerName: 'Charge', flex: 1, minWidth: 200 },
    { field: 'rate', headerName: 'Rate', width: 110 },
    { field: 'multiplier', headerName: 'Quantity', width: 110 },
    { field: 'net_amount', headerName: 'Amount', width: 120, valueFormatter: params =>
      typeof params.value === 'number' ? params.value.toFixed(2) : '' },
    { field: 'note', headerName: 'Note', flex: 1, minWidth: 200 }
  ];

  constructor(private reports: CustomerFinancialReportService) {}
  get busy(): boolean { return this.previewing || this.creating || this.resolving; }
  get hasDownload(): boolean { return this.completedDownload !== null; }
  ngOnInit(): void { this.loadBillingAccounts(); }
  ngOnDestroy(): void { this.listRequest?.unsubscribe(); this.actionRequest?.unsubscribe(); }
  gridReady(event: GridReadyEvent<ReportCandidate>): void { this.grid = event.api; }

  toggleTheme(): void {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
    localStorage.setItem('ulaap-customer-reports-theme', this.theme);
  }

  loadData(resetPage = false): void {
    if (this.busy) return;
    if (resetPage) { this.offset = 0; this.selectedOnly = false; }
    this.listRequest?.unsubscribe();
    this.loading = true; this.error = '';
    this.listRequest = this.reports.candidates(this.customer, this.offset, this.search).subscribe({
      next: result => {
        this.rowData = result.data; this.hasMore = result.meta.has_more; this.loading = false;
      },
      error: error => { this.loading = false; this.rowData = []; this.hasMore = false; void this.showError(error); }
    });
  }

  page(delta: number): void { this.offset = Math.max(0, this.offset + delta * 100); this.loadData(); }
  onCustomerSelected(option: ContactAutocompleteResult): void {
    if (this.busy || !option.value || option.value === this.customer) return;
    this.customer = option.value; this.customerName = option.label;
    this.selected.clear(); this.selectionChanged(); this.search = ''; this.selectedOnly = false;
    this.pasteText = ''; this.resetPasteFeedback(); this.loadBillingAccounts();
  }

  resetPasteFeedback(): void { this.pasteSummary = ''; this.pasteIssues = []; }

  selectPasted(): void {
    if (this.busy) return;
    this.resetPasteFeedback(); this.error = '';
    const identifiers = [...new Map(this.pasteText.split(/[\r\n\t,;]+/).map(value => value.trim())
      .filter(Boolean).map(value => [value.toLowerCase(), value])).values()];
    if (!identifiers.length || identifiers.length > 100) {
      this.error = 'Paste between 1 and 100 UUIDs or alternate references.';
      return;
    }
    this.resolving = true; this.setBusy(false, false);
    this.actionRequest = this.reports.resolve(this.customer, identifiers).subscribe({
      next: result => {
        this.resolving = false; this.setBusy(false, false);
        this.pasteIssues = result.results.filter(item => item.status !== 'matched');
        const additions = result.data.filter(row => !this.selected.has(row.lading_uuid));
        if (this.selected.size + additions.length > 100) {
          this.error = `Found ${additions.length} new ladings, but only ${100 - this.selected.size} spaces remain. Clear or reduce the selection and try again. No selections were changed.`;
          return;
        }
        const existing = result.data.length - additions.length;
        for (const row of result.data) this.selected.set(row.lading_uuid, row);
        if (additions.length) this.selectionChanged();
        this.pasteSummary = `${additions.length} added · ${existing} already selected · ${this.pasteIssues.length} unresolved`;
        if (result.data.length) { this.selectedOnly = true; this.grid?.setFilterModel(null); }
      },
      error: error => { this.resolving = false; this.setBusy(false, false); void this.showError(error); }
    });
  }

  private selectionButton(params: ICellRendererParams<ReportCandidate>): HTMLElement {
    const button = document.createElement('button');
    const row = params.data;
    button.type = 'button'; button.className = 'cfr-grid-button';
    button.textContent = row && this.selected.has(row.lading_uuid) ? 'Deselect' : 'Select';
    button.disabled = !row || this.busy || (!this.selected.has(row.lading_uuid) && (!row.eligible || this.selected.size >= 100));
    button.title = row?.exclusion_reason || '';
    button.onclick = () => { if (row) this.toggleSelection(row); };
    return button;
  }

  toggleSelection(row: ReportCandidate): void {
    if (this.busy) return;
    if (this.selected.has(row.lading_uuid)) this.selected.delete(row.lading_uuid);
    else if (row.eligible && this.selected.size < 100) this.selected.set(row.lading_uuid, row);
    this.selectionChanged();
  }

  selectVisible(): void {
    if (this.busy) return;
    this.grid?.forEachNodeAfterFilterAndSort(node => {
      if (node.data?.eligible && this.selected.size < 100) this.selected.set(node.data.lading_uuid, node.data);
    });
    this.selectionChanged();
  }

  clearSelection(): void {
    if (!this.busy) { this.selected.clear(); this.selectedOnly = false; this.selectionChanged(); this.resetPasteFeedback(); }
  }
  private selectionChanged(): void {
    this.selectedRows = [...this.selected.values()];
    this.preview = null; this.requestKey = ''; this.completedDownload = null; this.success = ''; this.error = '';
    this.grid?.refreshCells({ columns: ['selection'], force: true });
  }

  private setBusy(previewing: boolean, creating: boolean): void {
    this.previewing = previewing; this.creating = creating;
    this.busy ? this.customerControl.disable({ emitEvent: false }) : this.customerControl.enable({ emitEvent: false });
    this.grid?.refreshCells({ columns: ['selection'], force: true });
  }

  previewSelection(): void {
    if (!this.selected.size || this.busy) return;
    this.error = ''; this.success = ''; this.setBusy(true, false);
    this.actionRequest = this.reports.preview(this.customer, [...this.selected.keys()].sort()).subscribe({
      next: result => {
        if (this.preview?.preview_hash !== result.preview_hash || !this.requestKey) this.requestKey = crypto.randomUUID();
        this.preview = result; this.completedDownload = null;
        this.setBusy(false, false);
      },
      error: error => { this.preview = null; this.setBusy(false, false); void this.showError(error); }
    });
  }

  createReport(): void {
    if (!this.preview?.can_generate || this.busy || this.completedDownload) return;
    this.error = ''; this.setBusy(false, true);
    this.actionRequest = this.reports.generate(this.customer, [...this.selected.keys()].sort(), this.preview.preview_hash, this.requestKey).subscribe({
      next: response => {
        this.setBusy(false, false);
        if (!response.body) { this.error = 'The report download was empty. Retry the same selection.'; return; }
        const number = response.headers.get('X-Statement-Number') || 'report';
        this.completedDownload = response.body;
        this.completedFilename = `${this.customerName.replace(/[^a-z0-9_-]/gi, '_')}_statement_${number}.csv`;
        this.success = `Statement ${number} created for ${this.preview?.shipment_count} ladings.`;
        this.downloadAgain(); this.loadData();
      },
      error: error => { this.setBusy(false, false); void this.showError(error); }
    });
  }

  downloadAgain(): void { if (this.completedDownload) saveAs(this.completedDownload, this.completedFilename); }

  private async showError(error: { status?: number; error?: unknown }): Promise<void> {
    let payload = error.error;
    if (payload instanceof Blob) {
      try { payload = JSON.parse(await payload.text()); } catch { payload = null; }
    }
    const detail = payload as { message?: string; error?: string } | null;
    this.error = detail?.message || `Could not complete the report request${error.status ? ` (${error.status})` : ''}.`;
    if (detail?.error === 'preview_changed' || detail?.error === 'ineligible_selection') {
      this.preview = null; this.requestKey = '';
    }
  }
}
